import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { parseSpokenTime } from '../../utils/parseSpokenTime';

const API = window.__WR_API_BASE__ || '';
const MAX_RECORD_MS = 6000;   // Auto-stop after 6 sec — spoken time is short.
const MIN_VALID_BYTES = 1024; // Match backend guard.

/**
 * DosTimeMicButton — Mar 3, 2026
 *
 * Small mic button that sits next to the DOS Time input in the setup
 * header. Tap → records ~6s of audio → sends to `/api/transcribe`
 * (Deepgram Nova-3, solo mode) → parses spoken phrase via
 * `parseSpokenTime` → calls `onTime("HH:MM")` on success.
 *
 * Rationale: providers back-charting in a busy clinic don't want to
 * fumble numeric keypads. Speaking "eleven forty-five" is faster.
 *
 * Failure behavior is intentionally loud (toast + no state write) so
 * the wrong time never sneaks into a clinical note.
 */
const DosTimeMicButton = ({ onTime, disabled }) => {
  const [state, setState] = useState('idle');   // idle | recording | processing
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const autoStopTimerRef = useRef(null);
  const streamRef = useRef(null);

  const cleanupStream = useCallback(() => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    } catch { /* ignore */ }
  }, []);

  const stopRecording = useCallback(() => {
    if (autoStopTimerRef.current) {
      clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== 'inactive') {
      try { rec.stop(); } catch { /* ignore */ }
    }
  }, []);

  const startRecording = useCallback(async () => {
    if (disabled || state !== 'idle') return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        if (MediaRecorder.isTypeSupported('audio/mp4')) mimeType = 'audio/mp4';
        else if (MediaRecorder.isTypeSupported('audio/ogg')) mimeType = 'audio/ogg';
        else mimeType = '';
      }
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        cleanupStream();
        const blob = new Blob(audioChunksRef.current, {
          type: recorder.mimeType || 'audio/webm',
        });
        audioChunksRef.current = [];

        if (blob.size < MIN_VALID_BYTES) {
          setState('idle');
          toast.error('Didn\'t catch that — try speaking a bit longer.', { duration: 4000 });
          return;
        }

        setState('processing');
        try {
          const formData = new FormData();
          let ext = 'webm';
          if (blob.type.includes('mp4')) ext = 'mp4';
          else if (blob.type.includes('ogg')) ext = 'ogg';
          formData.append('file', blob, `dos-time.${ext}`);
          formData.append('recording_mode', 'solo');

          const resp = await axios.post(`${API}/api/transcribe`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 20000,
          });
          const rawText = (resp?.data?.text || '').trim();
          if (!rawText) {
            toast.error('No speech detected. Try again — say "eleven forty-five".', { duration: 4500 });
            setState('idle');
            return;
          }
          const parsed = parseSpokenTime(rawText);
          if (!parsed) {
            toast.error(`Couldn\'t parse "${rawText}" as a time. Try "eleven forty-five" or "oh nine hundred".`, { duration: 5500 });
            setState('idle');
            return;
          }
          onTime && onTime(parsed);
          toast.success(`DOS time set to ${parsed}`, { duration: 2500 });
        } catch (err) {
          const status = err?.response?.status;
          const detail = err?.response?.data?.detail;
          if (status === 400 && typeof detail === 'string') {
            toast.error(detail, { duration: 4500 });
          } else {
            toast.error('Voice time input failed. Please type the time manually.', { duration: 4500 });
          }
        } finally {
          setState('idle');
        }
      };

      recorder.onerror = () => {
        cleanupStream();
        setState('idle');
        toast.error('Mic error — please type the time manually.', { duration: 4500 });
      };

      recorder.start();
      setState('recording');

      autoStopTimerRef.current = setTimeout(() => {
        stopRecording();
      }, MAX_RECORD_MS);
    } catch (err) {
      cleanupStream();
      setState('idle');
      if (err && (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError')) {
        toast.error('Microphone access denied. Allow mic to use voice time input.', { duration: 6000 });
      } else if (err && err.name === 'NotFoundError') {
        toast.error('No microphone found on this device.', { duration: 5000 });
      } else {
        toast.error(`Couldn't access mic: ${err?.message || err?.name || 'unknown error'}`, { duration: 5000 });
      }
    }
  }, [disabled, state, cleanupStream, onTime, stopRecording]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      if (autoStopTimerRef.current) clearTimeout(autoStopTimerRef.current);
      const rec = mediaRecorderRef.current;
      if (rec && rec.state !== 'inactive') {
        try { rec.stop(); } catch { /* ignore */ }
      }
      cleanupStream();
    };
  }, [cleanupStream]);

  const handleClick = () => {
    if (state === 'idle') startRecording();
    else if (state === 'recording') stopRecording();
    // 'processing' is a no-op (transcription in flight).
  };

  const isRecording = state === 'recording';
  const isProcessing = state === 'processing';
  const title = isRecording
    ? 'Recording… tap to stop. Say "eleven forty-five", "oh nine hundred", or "1145".'
    : isProcessing
      ? 'Transcribing…'
      : 'Speak the DOS time. Say "eleven forty-five", "oh nine hundred", or "1145".';

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || isProcessing}
      title={title}
      aria-label={title}
      data-testid="setup-header-dos-time-mic"
      className={`inline-flex items-center justify-center h-8 w-8 rounded border transition-colors shrink-0 ${
        isRecording
          ? 'border-red-500/70 bg-red-500/20 text-red-200 animate-pulse'
          : isProcessing
            ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-200'
            : 'border-border bg-secondary/40 text-muted-foreground hover:bg-secondary hover:text-foreground'
      }`}
    >
      {isProcessing ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <Mic className="w-3.5 h-3.5" />
      )}
    </button>
  );
};

export default DosTimeMicButton;
